import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sun, Moon, LogOut, Command, PenLine, HelpCircle, Menu } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useBranding } from "@/context/BrandingContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import CommandPalette from "@/components/CommandPalette";
import SignaturePad from "@/components/SignaturePad";
import ToothIcon from "@/components/ToothIcon";
import Sidebar from "@/components/Sidebar";
import TabsBar from "@/components/TabsBar";
import GuidedTour from "@/components/GuidedTour";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MobileNavDrawer, MobileBottomNav } from "@/components/MobileNav";
import { ROLE_LABEL } from "@/lib/api";

export default function Layout({ children }) {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (user && user.onboarding_done !== true) {
      const t = setTimeout(() => setTourOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [user?.id]); // eslint-disable-line

  const closeTour = async () => {
    setTourOpen(false);
    if (user && user.onboarding_done !== true) {
      try { await api.post("/users/me/onboarding-done"); await refresh(); } catch { /* noop */ }
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar fixed */}
      <Sidebar />

      {/* Main column */}
      <div className="md:ml-16 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 border-b border-border glass">
          <div className="h-full px-3 sm:px-6 flex items-center gap-2 sm:gap-3">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMenuOpen(true)}
              className="md:hidden w-10 h-10 rounded-sm border border-border bg-card hover:bg-muted flex items-center justify-center shrink-0"
              aria-label="Abrir menú"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mr-1 min-w-0">
              {branding.logo_principal_b64 ? (
                <img src={branding.logo_principal_b64} alt={branding.nombre_comercial} className="h-7 max-w-[120px] sm:max-w-[160px] object-contain" />
              ) : branding.logo_cuadrado_b64 ? (
                <img src={branding.logo_cuadrado_b64} alt={branding.nombre_comercial} className="w-7 h-7 rounded-sm object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-primary)", color: "#fff" }}>
                  <ToothIcon className="w-4 h-4" />
                </div>
              )}
              {!branding.logo_principal_b64 && (
                <div className="hidden sm:block font-display font-bold tracking-tight text-base">{branding.nombre_comercial || "DentCloud"}</div>
              )}
            </div>

            <div className="flex-1" />

            <button
              data-testid="cmd-k-trigger"
              onClick={() => setCmdOpen(true)}
              className="flex items-center gap-2 h-10 w-10 md:w-auto justify-center md:justify-start px-0 md:px-3 rounded-sm border border-border bg-card hover:bg-muted text-muted-foreground transition"
              aria-label="Buscar (Ctrl/Cmd + K)"
            >
              <Search className="w-4 h-4" />
              <span className="hidden lg:inline text-sm">Buscar pacientes, citas…</span>
              <span className="hidden md:flex ml-1 items-center gap-1 text-[10px] font-mono bg-muted text-muted-foreground rounded-sm px-1.5 py-0.5 border border-border">
                <Command className="w-3 h-3" /> K
              </span>
            </button>

            <button
              data-testid="tour-trigger"
              onClick={() => setTourOpen(true)}
              className="h-10 px-3 rounded-sm border border-border bg-card hover:bg-muted flex items-center gap-2 transition text-sm"
              aria-label="Guía de la plataforma"
              title="Guía paso a paso"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden lg:inline">Guía</span>
            </button>

            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className="hidden sm:flex w-10 h-10 rounded-sm border border-border bg-card hover:bg-muted items-center justify-center transition"
              aria-label="Cambiar tema"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <button
              data-testid="my-signature-btn"
              onClick={() => setSigOpen(true)}
              className="hidden md:flex w-10 h-10 rounded-sm border border-border bg-card hover:bg-muted items-center justify-center transition"
              aria-label="Mi firma"
              title="Mi firma"
            >
              <PenLine className="w-4 h-4" />
            </button>

            <div className="hidden md:flex items-center gap-2 pl-2 border-l border-border ml-1">
              <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center font-display font-bold text-xs">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 max-w-[140px]">
                <div className="text-xs font-semibold truncate">{user?.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
              <button
                data-testid="logout-button-top"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="p-2 rounded-sm hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition"
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Tabs bar (sticky, debajo del header) */}
        <div className="sticky top-16 z-20 bg-background">
          <TabsBar />
        </div>

        {/* Content */}
        <main className="flex-1 px-4 sm:px-8 py-6 sm:py-8 pb-24 md:pb-10">
          <div className="reveal">{children}</div>
        </main>
      </div>

      <MobileBottomNav />
      <MobileNavDrawer open={menuOpen} onOpenChange={setMenuOpen} onSearch={() => setCmdOpen(true)} onSignature={() => setSigOpen(true)} />
      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} />
      <GuidedTour open={tourOpen} onClose={closeTour} />

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="my-signature-dialog">
          <DialogHeader>
            <div className="label-eyebrow text-primary mb-1">Mi firma</div>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">Firma manuscrita</DialogTitle>
            <DialogDescription className="text-xs">Aparecerá en evoluciones, presupuestos, recibos y cierres de caja que tú generes.</DialogDescription>
          </DialogHeader>
          <div>
            <SignaturePad
              initialValue={user?.signature_image || null}
              onSave={async (dataUrl) => {
                try {
                  await api.put("/users/me/signature", { signature_image: dataUrl });
                  toast.success("Firma guardada");
                  await refresh();
                  setSigOpen(false);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar la firma");
                }
              }}
              onClear={async () => {
                if (!window.confirm("¿Eliminar tu firma guardada?")) return;
                try {
                  await api.delete("/users/me/signature");
                  toast.success("Firma eliminada");
                  await refresh();
                  setSigOpen(false);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
                }
              }}
              onCancel={() => setSigOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
